import { atom } from "jotai";
import { CrossDomainStorage } from "../../helpers/crossDomainStorage";
import { globalJotaiStore } from "../../jotai-store";
import type { CookieConsentValue } from "./consent";
import { getCookie } from "./cookies";
import { updateGtagConsent } from "./gtag-consent";

export type OnesaasConsent = {
  onesaascookie_acceptance: CookieConsentValue;
  onesaascookie_performance: CookieConsentValue;
  onesaascookie_targeting: CookieConsentValue;
};

const defaultConsent: OnesaasConsent = {
  onesaascookie_acceptance: "unset",
  onesaascookie_performance: "unset",
  onesaascookie_targeting: "unset",
};

export const onesaasConsentAtom = atom<OnesaasConsent>(defaultConsent);

const ONESAAS_COOKIE_CONSENT = "onesaascookieconsent";

export const getOnesaasConsent = (): OnesaasConsent => {
  const onesaasConsent = getCookie(ONESAAS_COOKIE_CONSENT);

  if (onesaasConsent) {
    return onesaasConsent.split("&").reduce(
      (mappedValues, keyvalue) => {
        const [key, ...values] = keyvalue.split("=");
        if (!key) {
          return mappedValues;
        }
        return { ...mappedValues, [key]: values.join("") };
      },
      {
        ...defaultConsent,
      }
    );
  }

  return { ...defaultConsent };
};

export const updateOnesaasConsentCookie = (
  acceptance: CookieConsentValue,
  performance: CookieConsentValue,
  targeting: CookieConsentValue
) => {
  updateGtagConsent(acceptance, performance, targeting);

  const newOnesaasConsent: OnesaasConsent = {
    onesaascookie_acceptance: acceptance,
    onesaascookie_performance: performance,
    onesaascookie_targeting: targeting,
  };

  globalJotaiStore.set(onesaasConsentAtom, newOnesaasConsent);

  const newCookieValue = Object.entries(newOnesaasConsent).reduce<string>(
    (acc, [key, value], i) => `${acc}${i !== 0 ? "&" : ""}${key}=${value}`,
    ""
  );

  const yearInSecondsString = (60 * 60 * 24 * 365).toString();
  document.cookie =
    ONESAAS_COOKIE_CONSENT +
    "=" +
    newCookieValue +
    "; max-age=" +
    yearInSecondsString;
  CrossDomainStorage.getInstance().append(
    ONESAAS_COOKIE_CONSENT,
    newCookieValue
  );
};

export const syncSharedStorageConsent = async () => {
  const crossDomainStorage = CrossDomainStorage.getInstance();
  await crossDomainStorage.copyCookiesToLocal([ONESAAS_COOKIE_CONSENT]);
};
